import { postLifeEvent } from '../../../../../global/ducks/mlps-api';
import { getFormattedDateYmd } from '../../../../../global/helpers/date-helpers';
import { getCurrencyFormat } from '../../../../../global/helpers/format-helpers';
import { CoveredPerson, Plan, PlanExtended } from '../../../../../Global/models/coverage-model';
import { LabelSettingProps } from '../../../../../global/models/epi-model';
import { SelfText } from '../../../../annual-enrollment/models/annual-enrollment-models';
import { getRelationshipLabel, getTierLabel } from '../../../../arc/features/new-hire-enrollment/enrollment/helpers/coverage-display-helpers';
import { AdminEnrollAllBehalfOfMemberPost } from '../../../../arc/features/new-hire-enrollment/enrollment/helpers/coverage-form-helpers';
import { CoverageFormModel, CoveragePerson, CoveragePlans, NewHireCoverageState, PlanTypes, TiersTypes } from '../../../../arc/features/new-hire-enrollment/enrollment/models/coverage-models';
import { CalculatedTiers } from '../../../../arc/features/new-hire-enrollment/models/new-hire-enrollment-models';
import { BenefitEnrollmentStep } from '../../../../arc/features/new-hire-enrollment/models/pension-enrollment-models';
import { setCurrentNewHireBenefitEnrollmentStep } from '../../../../arc/features/new-hire-enrollment/slice/new-hire-enrollment-slices';
import { BillingAccountOptionValues } from '../../../../arc/features/new-participant-enrollment/models/form-options';
import { NewHireProps } from '../../../../arc/models/arc-epi-props';
import { MidYearConfirmationModel, MyCpgMidYearCreateEnrollmentPayload, MyCpgMidYearInitialValues } from '../models/mycpg-mid-year-models';


export function onSubmitCoverage(
    dispatch: any,
    step: BenefitEnrollmentStep,
    onSuccess: any,
    values: CoverageFormModel,
    employeeId: string,
    employerId: string,
    formId: number,
    displayTierMedical: CalculatedTiers,
    displayTierDental: CalculatedTiers,
    coverageState: NewHireCoverageState,
) {
    if (step === BenefitEnrollmentStep.COVERAGE) {
        dispatch(setCurrentNewHireBenefitEnrollmentStep(BenefitEnrollmentStep.REVIEW));
        return;
    }

    AdminEnrollAllBehalfOfMemberPost(
        dispatch,
        values,
        coverageState,
        employeeId,
        employerId,
        displayTierMedical,
        displayTierDental,
        null,
        null,
        formId,
        (res) => onSuccess(res),
        null,
    );
}


export function createEnrollmentWindow(guid: string, payload: MyCpgMidYearCreateEnrollmentPayload, onSuccess, onFailure, dispatch: any) {
    dispatch(postLifeEvent(guid, payload, onSuccess, onFailure));
}
export function GetCoveredPeople(newHireSettings: NewHireProps, eligibleDependents: CoveragePerson[], selectedDependents: string[]): CoveredPerson[] {
    const coveredPeople: CoveredPerson[] = [];

    selectedDependents?.forEach(
        (selectedDependent) => {

            if (selectedDependent?.toLowerCase() === 'self') {
                coveredPeople.push({
                    name: newHireSettings?.selfOptionText ?? 'Self',
                    relationship: '',
                });
                return;
            }

            const foundDependent = eligibleDependents.find((eligibleDependent) => eligibleDependent.guid === selectedDependent);

            if (foundDependent) {
                coveredPeople.push({
                    name: `${foundDependent.first_name}${foundDependent.middle_name ? ` ${foundDependent.middle_name} ` : ' '}${foundDependent.last_name}`,
                    relationship: getRelationshipLabel(foundDependent?.relationship, newHireSettings),
                });
            }
        });


    return coveredPeople;
}

export function GetCoveredPeopleMedical(newHireSettings: NewHireProps, coverageState: NewHireCoverageState, submittedValues: CoverageFormModel): CoveredPerson[] {
    return GetCoveredPeople(newHireSettings, coverageState?.eligible_dependents, submittedValues?.medicalDependents);
}

export function GetCoveredPeopleDental(newHireSettings: NewHireProps, coverageState: NewHireCoverageState, submittedValues: CoverageFormModel): CoveredPerson[] {
    return GetCoveredPeople(newHireSettings, coverageState?.eligible_dependents, submittedValues?.dentalDependents);
}

export function GetReviewPlan(
    newHireSettings: NewHireProps,
    labelSettings: LabelSettingProps,
    planCode: string,
    plans: CoveragePlans,
    tier: CalculatedTiers): Plan {

    const selectedPlan = plans?.plans?.find(p => p?.code === planCode);

    if (selectedPlan) {

        return {
            code: selectedPlan?.code,
            planName: selectedPlan.name,
            planTier: getTierLabel(tier?.display_tier as TiersTypes, newHireSettings, labelSettings, selectedPlan?.rates_by_tier),
            planPrice: selectedPlan?.rates_by_tier && getCurrencyFormat(selectedPlan?.rates_by_tier[tier?.display_tier], labelSettings, true),
            planPdf: selectedPlan.pdf,
        };
    }

    return null;
}

export function GetReviewPlanExtended(
    newHireSettings: NewHireProps,
    labelSettings: LabelSettingProps,
    planCode: string,
    plans: CoveragePlans,
    tier: CalculatedTiers,
    billingAccountKey: string,
    submittedValues: CoverageFormModel): PlanExtended {

    const selectedPlan = plans?.plans?.find(p => p?.code === planCode);

    if (!selectedPlan) return null;

    const reviewPlan = GetReviewPlan(newHireSettings, labelSettings, planCode, plans, tier);

    const extendedPlan: PlanExtended = {
        ...reviewPlan,
        accountNumber: (submittedValues[billingAccountKey] === BillingAccountOptionValues.INSTITUTION) ? plans?.account_number : plans?.diocese_account_number,
        effectiveDate: getFormattedDateYmd(plans?.effective_date, true),
        groupId: plans?.group_id,
        volume: selectedPlan?.volume,
        rate: selectedPlan?.rate,
    };

    return extendedPlan;
}

export function PrepareMidYearConfirmationModel(
    newHireSettings: NewHireProps,
    labelSettings: LabelSettingProps,
    submittedValues: CoverageFormModel,
    coverageState: NewHireCoverageState,
    displayTierMedical: CalculatedTiers,
    displayTierDental: CalculatedTiers,
    qualifyingEvent?: string,
    qualifyingEventDate?: string,
): MidYearConfirmationModel {

    const coveredPeopleMedical: CoveredPerson[] = GetCoveredPeopleMedical(null, coverageState, submittedValues);
    const coveredPeopleDental: CoveredPerson[] = GetCoveredPeopleDental(null, coverageState, submittedValues);

    const selectedMedicalPlan = GetReviewPlan(newHireSettings, labelSettings, submittedValues[PlanTypes.M], coverageState?.eligible_plans?.medical, displayTierMedical);
    const selectedDentalPlan = GetReviewPlan(newHireSettings, labelSettings, submittedValues[PlanTypes.D], coverageState?.eligible_plans?.dental, displayTierDental);

    const selectedGroupLifePlan = GetReviewPlanExtended(newHireSettings, labelSettings, submittedValues[PlanTypes.G], coverageState?.eligible_plans?.group_life, displayTierMedical, 'G_billingAccountType', submittedValues);
    const selectedShortTermDisability = GetReviewPlanExtended(newHireSettings, labelSettings, submittedValues[PlanTypes.W], coverageState?.eligible_plans?.group_life, displayTierMedical, 'W_billingAccountType', submittedValues);
    const selectedLongTermDisability : PlanExtended = GetReviewPlanExtended(newHireSettings, labelSettings, submittedValues[PlanTypes.L], coverageState?.eligible_plans?.group_life, displayTierMedical, 'L_billingAccountType', submittedValues);
    const selectedUei = GetReviewPlanExtended(newHireSettings, labelSettings, submittedValues[PlanTypes.U], coverageState?.eligible_plans?.group_life, displayTierMedical, 'U_billingAccountType', submittedValues);

    const { medicalDeclineReason, medicalDeclineReasonOther, dentalDeclineReason, dentalDeclineReasonOther } = submittedValues;

    const returnModel: MidYearConfirmationModel = {
        hasMedicalAvailable: coverageState?.eligible_plans?.medical?.plans?.length > 0,
        hasDentalAvailable: coverageState?.eligible_plans?.dental?.plans?.length > 0,
        hasGroupLife: coverageState?.eligible_plans?.group_life?.plans?.length > 0,
        hasShortTermDisability: coverageState?.eligible_plans?.short_term_disability?.plans?.length > 0,        
        hasLongTermDisability: coverageState?.eligible_plans?.long_term_disability?.plans?.length > 0,        
        hasUei: coverageState?.eligible_plans?.uei?.plans?.length > 0,
        hasMedicalEnrolled: selectedMedicalPlan?.planName && coveredPeopleMedical?.length > 0 && !medicalDeclineReason,
        hasDentalEnrolled: selectedDentalPlan?.planName && coveredPeopleDental?.length > 0 && !dentalDeclineReason,
        coveredPeopleMedical,
        coveredPeopleDental,
        selectedMedicalPlan,
        selectedDentalPlan,
        selectedGroupLifePlan,
        selectedShortTermDisability,
        selectedLongTermDisability,
        selectedUei,
        qualifyingEvent,
        qualifyingEventDate,
        medicalDeclineReason,
        medicalDeclineReasonOther,
        dentalDeclineReason,
        dentalDeclineReasonOther,
    };

    return returnModel;
}

export function GetNonRemovedDependents(removedDependentIds: string[], currentDependentIds: string[]): string[] {
    if (!currentDependentIds?.length) return []; // If currentDependentIds is empty, return an empty array
    
    return removedDependentIds != null ?
        currentDependentIds.filter(guid => !removedDependentIds.includes(guid)) :
        currentDependentIds;
}


//Modify type
export function GetMyCpgMidYearInitialValues(coverageState: NewHireCoverageState): MyCpgMidYearInitialValues {
    const ineligibleDepedentGuids = coverageState?.eligible_dependents?.filter(dep => dep.remove).map(dep => dep.guid); 
    return {

        selectedMedicalPlanCode: coverageState?.eligible_plans?.medical?.current?.code,
        selectedDentalPlanCode: coverageState?.eligible_plans?.dental?.current?.code,
        selectedGroupPlanCode: coverageState?.eligible_plans?.group_life?.current?.code ?? '',
        selectedLtdPlanCode: coverageState?.eligible_plans?.long_term_disability?.current?.code,
        selectedStdPlanCode: coverageState?.eligible_plans?.short_term_disability?.current?.code,
        selectedUeiPlanCode: coverageState?.eligible_plans?.uei?.current?.code,
        selectedMedicalPlanName: coverageState?.eligible_plans?.medical?.current?.name,
        selectedDentalPlanName: coverageState?.eligible_plans?.dental?.current?.name,
        selectedMedicalBillingAccountNumber: coverageState?.eligible_plans?.medical?.current?.account_number,
        selectedDentalBillingAccountNumber: coverageState?.eligible_plans?.dental?.current?.account_number,
        selectedGroupBillingAccountNumber: coverageState?.eligible_plans?.group_life?.current?.account_number,
        selectedLtdBillingAccountNumber: coverageState?.eligible_plans?.short_term_disability?.current?.account_number,
        selectedStdBillingAccountNumber: coverageState?.eligible_plans?.long_term_disability?.current?.account_number,
        selectedUeiBillingAccountNumber: coverageState?.eligible_plans?.uei?.current?.account_number,
        selectedMedicalTier: coverageState?.eligible_plans?.medical?.current?.tier,
        selectedDentalTier: coverageState?.eligible_plans?.dental?.current?.tier,
        medicalDependents: coverageState?.eligible_plans?.medical?.current?.code ? [SelfText].concat(GetNonRemovedDependents(ineligibleDepedentGuids, coverageState?.eligible_plans?.medical?.current?.dependents)) : [],
        dentalDependents: coverageState?.eligible_plans?.dental?.current?.code ? [SelfText].concat(GetNonRemovedDependents(ineligibleDepedentGuids, coverageState?.eligible_plans?.dental?.current?.dependents)) : [],
        medicalDeclineReason: coverageState?.eligible_plans?.medical?.current?.decline_reason,
        medicalDeclineReasonOther: '', //Decline Other Resaon it's not in the api signature given by CPG so it's not implemented yet
        dentalDeclineReason: coverageState?.eligible_plans?.dental?.current?.decline_reason,
        dentalDeclineReasonOther: '', //Decline Other Resaon it's not in the api signature given by CPG so it's not implemented yet
        specialRequests: coverageState?.special_requests,
        volume: coverageState?.eligible_plans?.group_life?.current?.volume ?? null,
    };
}